import { ImageSize, ProfileImageStack } from '@pbk-web/ui';
import type { FC } from 'react';
import { memo, useMemo } from 'react';

import { getEffectiveCriteriaOwnerId } from '../../../models/ClientFormUser';
import type { ClientFormUserRoleValues } from '../../../models/ClientFormUserRoles';
import { ClientFormUserRole } from '../../../models/ClientFormUserRoles';

type Prop = {
  userId: string;
  role: string;
  formSectionId?: string;
  acceptedStatus?: boolean;
  sortOrder?: number;
  requiresAction?: boolean;
  pbkUser: { firstName: string; lastName: string; userImageId: string };
};

export const GraphQLRoleToPlatformRole: Record<string, ClientFormUserRoleValues> = {
  OWNER: ClientFormUserRole.Owner,
  VALIDATOR: ClientFormUserRole.Validator,
  APPROVER: ClientFormUserRole.Approver,
  CONTRIBUTOR: ClientFormUserRole.Contributor,
  VIEWER: ClientFormUserRole.Viewer,
};

const MembersRenderer: FC<{ data: Prop[] | Prop }> = (props) => {
  const data = useMemo(() => (Array.isArray(props.data) ? props.data : [props.data]), [props.data]);

  const members = useMemo(() => {
    const mapped = [...data]
      .sort((a, b) => {
        // sort null formSectionIds first
        if (a.formSectionId && !b.formSectionId) {
          return 1;
        }
        if (!a.formSectionId && b.formSectionId) {
          return -1;
        }
        return 0;
      })
      .map((x) => ({
        id: x.userId,
        firstName: x.pbkUser?.firstName ?? '',
        lastName: x.pbkUser?.lastName ?? '',
        role: GraphQLRoleToPlatformRole[x.role],
        userImageId: x.pbkUser?.userImageId,
        acceptedStatus: x.acceptedStatus,
        sortOrder: x.sortOrder,
        formSectionId: x.formSectionId,
        requiresAction: x.requiresAction,
      }));

    // A form has a single owner, so multiple owner entries mean the owner was matched by a criteria
    // access-policy. GraphQL exposes no individual-vs-criteria flag, so collapse ALL owner entries to
    // the single effective owner (first id, string sort) per section — only that user is listed. Other
    // roles are untouched. Matches getEffectiveCriteriaOwnerId used on the REST path.
    const ownerIdsBySection = new Map<string, string[]>();
    for (const member of mapped) {
      if (member.role === ClientFormUserRole.Owner) {
        const sectionKey = member.formSectionId ?? '';
        ownerIdsBySection.set(sectionKey, [...(ownerIdsBySection.get(sectionKey) ?? []), member.id]);
      }
    }
    if (ownerIdsBySection.size === 0) return mapped;
    const effectiveOwnerBySection = new Map([...ownerIdsBySection].map(([sectionKey, ids]) => [sectionKey, getEffectiveCriteriaOwnerId(ids)]));
    return mapped.filter(
      (member) => member.role !== ClientFormUserRole.Owner || member.id === effectiveOwnerBySection.get(member.formSectionId ?? ''),
    );
  }, [data]);

  if (members.length === 0) return '-';

  return <ProfileImageStack users={members} size={ImageSize.XS} />;
};

export default memo(MembersRenderer);
